import { useDispatch, useSelector } from "@/types/store";
import * as chatGPTReducer from "@/containers/ChatGPT/reducer";
import { getModelOptions, MODELS } from "@/containers/ChatGPT/reducer";
import { getSelectedFolder } from "@/containers/Folders/reducer";
import { createTone, deleteTone, isDefaultTone, updateTone } from "@/containers/ChatTone/reducer";
import React, { useEffect, useState } from "react";
import * as loginReducer from "@/containers/Login/reducer";
import styles from "../style.module.css";
import { LineInput } from "@/components/Inputs/LineInput";
import Tooltip from "@/components/Inputs/Tooltip";
import { Button, Icon } from "@/aible";
import TextInput from "@/components/Inputs/TextInput";
import Select from "react-select";
export const ChatToneView = ({ tone }) => {
    const dispatch = useDispatch();
    const savedToneLabel = tone.name;
    const savedToneValues = tone.metadata.values;
    const savedToneModelKeys = Object.keys(savedToneValues);
    const savedVariants = [];
    let savedExclusions;
    const modelOptions = useSelector((state) => getModelOptions(state));
    const folderId = useSelector(getSelectedFolder);
    const DEFAULT_MODEL_SORT = MODELS.CHAT_GPT;
    // Iterate through existing tone values and group saved model variants by string equality. Variants are structured as { models: ModelOption[], instructions: string }
    if (savedToneModelKeys?.length > 0) {
        savedToneModelKeys.forEach((model) => {
            const retModelOption = modelOptions.find((o) => o.value === model) ?? {
                value: model,
                label: model
            };
            const foundVariant = savedVariants.find((v) => v.instructions === savedToneValues[model]);
            if (!(savedToneValues[model]?.length > 0)) {
                if (savedExclusions === undefined) {
                    savedExclusions = [retModelOption];
                }
                else {
                    savedExclusions.push(retModelOption);
                }
            }
            else if (foundVariant && retModelOption) {
                foundVariant.models.push(retModelOption);
            }
            else {
                const newVariant = {
                    models: [retModelOption],
                    instructions: savedToneValues[model]
                };
                savedVariants.push(newVariant);
            }
        });
    }
    // Sort all existing tone variants by largest model groups. If equal, sort the DEFAULT_MODEL_SORT first.
    savedVariants.sort((a, b) => {
        const aModels = a?.models;
        const bModels = b?.models;
        if (aModels?.length === bModels?.length) {
            const sortA = aModels?.some((model) => model?.value === DEFAULT_MODEL_SORT);
            const sortB = bModels?.some((model) => model?.value === DEFAULT_MODEL_SORT);
            return sortA ? -1 : sortB ? 1 : 0;
        }
        return b.models.length - a.models.length;
    });
    const isDefault = isDefaultTone(tone);
    const [isLoading, updateIsLoading] = useState(false);
    const userID = useSelector((state) => loginReducer.getUserID(state));
    const isToneCreator = "created_by" in tone && tone?.created_by?.id === userID;
    const disableDelete = isLoading || isDefault || !isToneCreator;
    const [toneLabel, updateToneLabel] = useState(savedToneLabel);
    const [variants, updateVariants] = useState(savedVariants);
    const [exclusions, updateExclusions] = useState(savedExclusions);
    // const [toneValues, updateToneValues] = useState(savedToneValues);
    const [isEdit, updateIsEdit] = useState(false);
    const deleteSavedChatTone = () => {
        updateIsLoading(true);
        dispatch(deleteTone(tone)).then(() => {
            updateIsLoading(false);
        });
    };
    const updateChatTone = (val) => dispatch(chatGPTReducer.updateChatTone(val));
    const updateSavedChatTone = () => {
        const retValues = {};
        // Apply the instructions from the first variant to all models first.
        // For each variant beyond the first, apply to models their instructions.
        if (variants?.length > 0) {
            variants.forEach((variant, index) => {
                if (index === 0) {
                    modelOptions.forEach((o) => {
                        retValues[o.value] = variant.instructions ?? "";
                    });
                }
                else if (variant?.models?.length > 0) {
                    variant?.models?.forEach((v) => {
                        if (v) {
                            retValues[v.value] = variant?.instructions ?? "";
                        }
                    });
                }
            });
        }
        // Set each exception model with blank instructions
        if (exclusions && exclusions?.length > 0) {
            exclusions.forEach((exclusion) => {
                retValues[exclusion.value] = "";
            });
        }
        const updatedTone = {
            ...tone,
            name: toneLabel ?? "New Chat Tone",
            metadata: {
                ...tone.metadata,
                values: retValues
            }
        };
        updateIsLoading(true);
        dispatch(updateTone(updatedTone)).finally(() => updateIsLoading(false));
    };
    const resetSavedTone = () => {
        updateToneLabel(savedToneLabel);
        updateVariants(savedVariants);
        updateExclusions(savedExclusions);
    };
    useEffect(() => {
        resetSavedTone();
    }, [tone]);
    const handleSaveNewtone = () => {
        updateIsLoading(true);
        const retValues = {};
        // Apply the instructions from the first variant to all models first.
        // For each variant beyond the first, apply to models their instructions.
        if (variants?.length > 0) {
            variants.forEach((variant, index) => {
                if (index === 0) {
                    modelOptions.forEach((o) => {
                        retValues[o.value] = variant.instructions ?? "";
                    });
                }
                else if (variant?.models?.length > 0) {
                    variant?.models?.forEach((v) => {
                        if (v) {
                            retValues[v.value] = variant?.instructions ?? "";
                        }
                    });
                }
            });
        }
        // Set each exception model with blank instructions
        if (exclusions && exclusions?.length > 0) {
            exclusions.forEach((exclusion) => {
                retValues[exclusion.value] = "";
            });
        }
        let retToneLabel = toneLabel && toneLabel?.length > 0 ? toneLabel : "New Chat Tone";
        if (!isEdit) {
            retToneLabel += " (Copy)";
        }
        const newTone = {
            folder_id: folderId,
            name: retToneLabel,
            metadata: {
                values: retValues
            }
        };
        // console.log("New tone is: ", newTone);
        updateIsLoading(true);
        dispatch(createTone(newTone)).then((tone) => {
            updateIsLoading(false);
            // console.log("Updating tone", tone);
            updateChatTone(tone?.id);
        });
    };
    const handleUpdateVariantKeyValue = (variant, key, index, val) => {
        const variantUpdate = {
            ...variant,
            [key]: val
        };
        const retVariants = variants?.slice();
        retVariants[index] = variantUpdate;
        updateVariants(retVariants);
    };
    const handleDeleteVariant = (index) => {
        const retVariants = variants?.slice();
        retVariants.splice(index, 1);
        updateVariants(retVariants);
    };
    const checkForEdits = () => {
        const editedName = toneLabel !== savedToneLabel;
        const editedVariantAmount = variants?.length !== savedVariants?.length;
        const editedVariantInstructions = variants?.some((variant, index) => variant?.instructions !== savedVariants[index]?.instructions);
        const editedModels = variants.some((variant, index) => variant.models.some((model, i) => {
            const refModel = savedVariants[index]?.models[i];
            if (variant?.models?.length !== savedVariants[index]?.models?.length) {
                return true;
            }
            if (model?.value !== refModel?.value) {
                return true;
            }
            return false;
        }));
        const editedExclusions = exclusions?.length !== savedExclusions?.length ||
            exclusions?.some((e, index) => !(savedExclusions && savedExclusions[index] && e?.value === savedExclusions[index]?.value));
        const hasEdits = editedName || editedVariantAmount || editedVariantInstructions || editedModels || editedExclusions;
        if (!isEdit && hasEdits) {
            updateIsEdit(true);
        }
        else if (isEdit && !hasEdits) {
            updateIsEdit(false);
        }
    };
    useEffect(() => {
        checkForEdits();
    }, [toneLabel, variants, exclusions]);
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.chatToneWrapper },
            React.createElement("div", { className: styles.chatToneTitle },
                "Tone name:",
                React.createElement(LineInput, { value: toneLabel, onChange: (name) => {
                        updateToneLabel(name);
                    }, isDisabled: isLoading, style: { backgroundColor: "white" } })),
            React.createElement("div", { className: styles.toneCreateSettingsWrapper },
                variants.map((variant, index) => {
                    if (index === 0) {
                        return (React.createElement("div", { className: styles.toneCreateItemWrapper, key: "variant__main" },
                            React.createElement("div", { className: styles.toneCreateLabelText }, "Tone Instructions"),
                            React.createElement(TextInput, { className: styles.chatToneTextInput, value: variant.instructions ?? "", onChange: (val) => {
                                    handleUpdateVariantKeyValue(variant, "instructions", index, val);
                                }, updateInstant: undefined, isDisabled: isLoading })));
                    }
                    return (React.createElement("div", { className: styles.toneCreateItemWrapper, key: variant?.models?.length > 0
                            ? "variant__" + variant?.models[0]?.label + "__" + index
                            : "variant__" + index },
                        React.createElement("div", { className: styles.toneCreateItemActionsWrapper },
                            React.createElement("div", { className: styles.toneCreateSelectWrapper },
                                React.createElement(Select, { value: variant.models ?? [], placeholder: "Select models for this variant", options: modelOptions.filter((o) => !variants?.some((variant, index) => {
                                        if (index === 0) {
                                            return false;
                                        }
                                        if (variant?.models?.some((v) => v?.value === o?.value)) {
                                            return true;
                                        }
                                        return false;
                                    })), onChange: (val) => {
                                        handleUpdateVariantKeyValue(variant, "models", index, val);
                                    }, isMulti: true, isDisabled: isLoading })),
                            React.createElement("div", null,
                                React.createElement(Button, { type: "secondary", onClick: () => {
                                        handleDeleteVariant(index);
                                    } }, "Delete Variant"))),
                        React.createElement("div", null,
                            React.createElement(TextInput, { className: styles.chatToneTextInput, value: variant?.instructions, onChange: (val) => {
                                    handleUpdateVariantKeyValue(variant, "instructions", index, val);
                                }, updateInstant: undefined, isDisabled: isLoading }))));
                }),
                React.createElement("div", { className: styles.toneCreateItemWrapper + " " + styles.toneCreateAddItemWrapper },
                    React.createElement("div", { className: styles.toneCreateAddItemLabel }, "Need to add instructions for specific model(s)?"),
                    React.createElement(Button, { type: "secondary", onClick: () => {
                            const newVariant = {
                                models: [],
                                instructions: ""
                            };
                            const retVariants = [...variants, newVariant];
                            updateVariants(retVariants);
                        }, isSmall: true, disabled: isLoading }, "Add LLM Specific Variant")),
                exclusions ? (React.createElement("div", { className: styles.toneCreateItemWrapper },
                    React.createElement("div", { className: styles.toneCreateLabelText }, "Exclude these models from this tone"),
                    React.createElement("div", { className: styles.toneCreateSelectWrapper },
                        React.createElement(Select, { value: exclusions ?? [], placeholder: "Select models for this variant", options: modelOptions?.filter((o) => !exclusions?.some((e) => e?.value === o?.value)), onChange: (val) => {
                                updateExclusions(val);
                            }, isMulti: true, isDisabled: isLoading })))) : (React.createElement("div", { className: styles.toneCreateItemWrapper + " " + styles.toneCreateAddItemWrapper },
                    React.createElement("div", { className: styles.toneCreateAddItemLabel }, "Want to prevent some models from using this tone?"),
                    React.createElement(Button, { type: "secondary", onClick: () => {
                            updateExclusions([]);
                        }, isSmall: true, disabled: isLoading }, "Select Models to Exclude"))))),
        React.createElement("div", { style: { display: "flex", justifyContent: isEdit ? "space-between" : "flex-end" } },
            isEdit && (React.createElement(Button, { isSmall: true, type: "secondary", onClick: () => {
                    resetSavedTone();
                }, disabled: isLoading }, "Cancel")),
            React.createElement("div", { className: styles.chatToneButtonWrapper },
                React.createElement(Tooltip, { trigger: isDefault || !isToneCreator ? ["hover"] : [], overlay: isDefault
                        ? "Your modified default tone must be saved as a new tone."
                        : "You must save your edits to a new tone." },
                    React.createElement(Button, { isSmall: true, type: "secondary", onClick: () => {
                            updateSavedChatTone();
                        }, disabled: isDefault || isLoading || !isToneCreator || !isEdit }, "Update")),
                React.createElement(Button, { isSmall: true, type: "secondary", onClick: () => {
                        handleSaveNewtone();
                    }, disabled: isLoading }, "Save New"),
                !isDefault && isToneCreator && (React.createElement(Button, { style: { padding: "8px 10px" }, isSmall: true, type: "secondary", onClick: deleteSavedChatTone, disabled: disableDelete },
                    React.createElement(Icon, { type: "trash", size: 13 })))))));
};
